import { useEffect, useRef } from 'react'
import { useGetCurrentLanguage } from 'src/store/game'
import { useClientSideTranslation } from 'src/services/i18n'
import { displayAmount, getCurrencyDecimals } from 'src/utils/displayAmount'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import { getDisplayMultiplier } from './useFrameBeacon'
import { sumRidingProjection } from './crashPayoutCeiling'
import type { PotentialWinReadout } from './crashChipLogic'

/**
 * The bottom-left overlay's "Potential Win" pill for crash (note c) — the
 * crash sibling of the chip-placement overlay's static Potential Win pill:
 *
 *   idle   — "—" (betting open, no bets, or the round busted with nothing
 *            banked).
 *   riding — lockedPayout + activeStake × the live DISPLAY multiplier,
 *            refreshed per frame via imperative textContent writes (the
 *            hero's pattern — zero re-renders). Reading the published
 *            display clock keeps this a pure ref read: no second beacon, no
 *            curve duplication, and the crash-hold freeze applies here for
 *            free — the figure can never settle before the viewer's frames.
 *   won    — every stake resolved with something banked: the label flips to
 *            "Won" and the combined realized payout shows in green.
 */

function formatMoney(value: number, currency: string): string {
  return `${getCurrencySymbol(currency)}${displayAmount(value, true, getCurrencyDecimals(value, currency))}`
}

function ridingValue(lockedPayout: number, activeStakes: number[], ceiling: number | null): number {
  const reading = getDisplayMultiplier()
  // A never-published clock renders the flat floor. A PARKED clock (the
  // crash-hold latches the publisher after the stamp) keeps its LAST value —
  // the same frozen figure the hero shows — so the pill freezes alongside it
  // instead of dropping to 1× (an ageMs fallback here would itself be a
  // tell: the drop would announce the publisher parking).
  const multiplier = reading && reading.value >= 1 ? reading.value : 1
  // Clamp PER BET, then sum — see sumRidingProjection. The arithmetic lives
  // there rather than inline so a test can assert WHERE the clamp lands, not
  // just that it exists.
  return sumRidingProjection(lockedPayout, activeStakes, multiplier, ceiling)
}

export function CrashPotentialPill({
  readout,
  currency,
  payoutCeiling = null,
  shortLabel = false
}: Readonly<{
  readout: PotentialWinReadout
  currency: string
  /** Operator liability ceiling in DECIMAL units (resolvePayoutCeiling), or
   * null when unconfigured. Clamps the riding projection only — realized
   * amounts already come from the backend. */
  payoutCeiling?: number | null
  /** Mobile renders the compact 'Win' label — 'Potential Win' wraps to two
   * lines in the narrow equal-width pills (PR #567, same as the
   * chip-placement sibling). */
  shortLabel?: boolean
}>) {
  const currentLanguage = useGetCurrentLanguage()
  const { t } = useClientSideTranslation(currentLanguage)
  const valueRef = useRef<HTMLSpanElement | null>(null)

  useEffect(() => {
    if (readout.kind !== 'riding') return undefined
    // Bind the span ONCE per effect run. The value span is keyed by
    // readout.kind, so on a riding→won flip React retargets the shared ref
    // to the NEW span during commit while this effect's final rAF may still
    // be pending — a per-frame ref read would smear a riding figure onto the
    // won span and React would never correct it. The captured old span is
    // detached after the swap, so a last in-flight write lands harmlessly.
    const el = valueRef.current
    if (!el) return undefined
    const { lockedPayout, activeStakes } = readout
    let raf = 0
    let lastText = ''
    const tick = () => {
      const text = formatMoney(ridingValue(lockedPayout, activeStakes, payoutCeiling), currency)
      if (text !== lastText) {
        lastText = text
        el.textContent = text
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [readout, currency, payoutCeiling])

  const isWon = readout.kind === 'won'
  const text =
    readout.kind === 'riding'
      ? formatMoney(
          ridingValue(readout.lockedPayout, readout.activeStakes, payoutCeiling),
          currency
        )
      : isWon || readout.kind === 'preview'
        ? formatMoney(readout.amount, currency)
        : '—'

  return (
    <div className="chip-stats-overlay__pill chip-stats-overlay__pill--potential">
      <span className="chip-stats-overlay__label">
        {isWon ? t('Won') : shortLabel ? t('Win') : t('Potential Win')}
      </span>
      <span aria-hidden className="chip-stats-overlay__divider" />
      {/* The shared --green treatment is the house "you'd win this" signal
          (chip-placement's Potential Win pill wears it too); the idle dash
          stays neutral. Keyed by state so React always replaces the text
          node when the state flips — the riding rAF mutates textContent
          behind React's back, and an unkeyed span could skip a reconcile
          onto stale text. */}
      <span
        className={`chip-stats-overlay__value${
          readout.kind === 'idle' ? '' : ' chip-stats-overlay__value--green'
        }`}
        key={readout.kind}
        ref={valueRef}
      >
        {text}
      </span>
    </div>
  )
}
